import React, { useContext, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAlert } from "react-alert";

import { SettingsContext } from "../contexts";
import RCListingForm from "../forms/RCListingForm.jsx";
import { PdfShower } from "../components";
import {
  FlowSection,
  AssignPersonPicker,
  SellRequestSearch,
  LinkedRequestSlot,
  IntakeFileBox,
  AiStatus,
  RepairToggleField,
  OwnedByField,
  IDoc,
  IUserIcon,
} from "./FlowKit.jsx";
import ContractsSection from "./ContractsSection.jsx";
import { Button, ButtonGroup, Radio, RadioGroup } from "../components/ui";
import ListingStatusSection from "./ListingStatusSection.jsx";
import useAutoContractDocs from "./useAutoContractDocs";
import {
  uploadIntakeFile,
  analyzeIntakeDocuments,
  resolveSuggestedProduct,
  fetchRequest,
  fetchPurchasingAgreement,
  fetchConsignmentAgreement,
  listingPrefillFromRequest,
  createDoneSellRequest,
  attachListingToRequest,
  sellRequestActionOnSave,
  activateListing,
} from "./intakeApi";

const personName = (u) =>
  [u?.first_name, u?.last_name].filter(Boolean).join(" ") || u?.email || (u?.id ? `User #${u.id}` : "");

/**
 * NEW STOCK — purpose-built create flow.
 *
 * UPLOAD: either a B2B supplier invoice (Gemini reads it → seller suggestions,
 * product auto-pick, field prefills) OR a Client PO (store-wide Sell Request
 * search → the request's Xero PO + client + product are pulled in).
 * ASSIGN SELLER: suggestions + full store user search + create-new.
 * Then the familiar sections; docs move into CONTRACTS; LISTING STATUS closes
 * with Save Draft / Publish / Create Repair.
 *
 * End of flow: supplier-invoice path also creates a linked Sell Request in
 * terminal ('done') status for the assigned seller; Client-PO path creates the
 * listing only (the request already exists).
 */
const RCStockListingCreate = () => {
  const history = useHistory();
  const alert = useAlert();
  const settingsCtx = useContext(SettingsContext);
  const iframeQueryParams = settingsCtx?.iframeQueryParams ?? settingsCtx?.settings?.iframeQueryParams ?? "";

  // ── intake state ────────────────────────────────────────────────────────────
  const [openSection, setOpenSection] = useState("upload");
  // Which of the two sources the rep is working in. Mirrors ASSIGN CONSIGNOR's
  // radio cards: the choice is explicit, and only the chosen one's fields are on
  // screen. Defaults to the supplier invoice — the common case for bought-in
  // stock, and the reason this flow exists.
  const [source, setSource] = useState("b2b"); // 'b2b' | 'po'
  const [invoiceFile, setInvoiceFile] = useState(null);        // {id, name}
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null); // full GET /api/requests/{id}
  const [aiState, setAiState] = useState(null);                 // loading | done | unavailable
  const [aiFields, setAiFields] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  // A read that succeeded but produced nothing to show. Measured on the two
  // OUTPUTS the rep can see — the suggestion chips and the prefilled fields —
  // rather than on the product, which they may have picked themselves.
  const aiEmpty = !suggestions.length && !aiFields;
  const [seller, setSeller] = useState(null);                   // {id, name}
  const [product, setProduct] = useState(null);                 // full product payload
  const [agreements, setAgreements] = useState({});             // {purchasing:{...}, consignment:{...}}
  // Whether a Sell Request will be created on save (supplier-invoice intake, or
  // an explicit "Create Sell Request" in Internal Details). Never both this and
  // a selected request — a listing links to exactly one.
  const [createRequest, setCreateRequest] = useState(false);
  // A request created mid-flow — or the Client PO the rep picked — exists before
  // the listing does, so its link is made once the listing has an id. Holds that
  // request's id until then.
  const [pendingAttachId, setPendingAttachId] = useState(null);
  // The request THIS flow raised, if any. One listing raises at most one, and
  // `pendingAttachId` cannot carry that rule on its own: unlinking clears it,
  // and the save path would then create a SECOND request for a seller who
  // already has one. This latches instead — never cleared once set.
  const [createdRequestId, setCreatedRequestId] = useState(null);
  const intentRef = useRef("draft");
  // "In Repair" — page state so the intake-section toggle (below) and the
  // Cost-tab toggle stay in lockstep: `enforce` mirrors this into Formik's
  // is_repair, `reportFields`/`onReport` mirror the Cost-tab value back.
  const [isRepair, setIsRepair] = useState(false);
  // "Owned By" — same arrangement, one field shared by the intake dropdown
  // (below) and the Listings-section dropdown inside Formik. Seeded to "owned":
  // the store buys this stock in. Never a second source of truth — see `enforce`
  // / `reportFields` below.
  const [ownedBy, setOwnedBy] = useState("owned");

  const toggleSection = (key) => setOpenSection((s) => (s === key ? null : key));

  // Switching source resets what the other branch had gathered, the same way
  // ASSIGN CONSIGNOR's option switch does — a listing links to exactly one
  // source, so leaving the other's state behind would quietly keep it as the
  // thing that gets saved. The assigned seller deliberately survives: it may
  // still be the right person, and it is a click to change.
  const SOURCES = [
    {
      key: "b2b",
      label: "B2B — Supplier Invoice",
      hint: "Upload the invoice; our image recognition model reads it and suggests the seller and product.",
    },
    {
      key: "po",
      label: "Client — PO",
      hint: "Link a Sell Request already in the store; its PO, client and product carry over.",
    },
  ];

  const pickSource = (key) => {
    if (key === source) return;
    setSource(key);
    setInvoiceFile(null);
    setSelectedRequest(null);
    // The link goes with the route being abandoned, falling back to a request
    // THIS flow raised: that one belongs to the listing either way.
    setPendingAttachId(createdRequestId);
    setAiState(null);
    setAiFields(null);
    setSuggestions([]);
    setAgreements({});
    setCreateRequest(false);
  };

  const advanceToSeller = () => setOpenSection("seller");

  // Hand over to the FORM's accordion (a separate one from the intake sections).
  const [formSectionRequest, setFormSectionRequest] = useState(null);
  const openFormSection = (key) =>
    setFormSectionRequest((prev) => ({ key, nonce: (prev?.nonce ?? 0) + 1 }));

  const advanceToListings = () => {
    setOpenSection(null); // collapse UPLOAD + ASSIGN SELLER
    openFormSection("listings");
  };

  // UPLOAD is committed by an explicit Continue, never by the upload or the
  // request selection resolving. The section's last field is the In Repair
  // toggle, and an implicit collapse took it off screen the instant the invoice
  // finished uploading — the rep could not reach it at all.
  //
  // Where Continue goes depends on how much the source already answered:
  //  - supplier invoice → ASSIGN SELLER. Gemini only *suggests* a seller, so
  //    that is still a decision the rep has to make.
  //  - Sell Request     → straight to LISTINGS, but only once it actually
  //    resolved the client AND the product: both intervening sections are then
  //    answered, and stopping on them would be two clicks of nothing to do.
  //    They stay collapsed and carry a summary of what was filled in. A
  //    summary-only load (the fetch failed) resolves neither, so it stops at
  //    ASSIGN SELLER rather than skipping past an unanswered section.
  const continueFromUpload = () => {
    if (selectedRequest && seller?.id && product?.id) advanceToListings();
    else advanceToSeller();
  };

  // ── Field 1: B2B supplier invoice ──────────────────────────────────────────
  const onInvoicePick = async (file) => {
    setUploadingInvoice(true);
    try {
      const uploaded = await uploadIntakeFile(file);
      setInvoiceFile(uploaded);
      // Either field 1 OR field 2 — picking the invoice clears the PO side.
      setSelectedRequest(null);
      setAgreements({});
      // Supplier-invoice intake ends by creating the seller's Sell Request.
      setCreateRequest(true);

      setAiState("loading");
      const result = await analyzeIntakeDocuments("supplier_invoice", [uploaded.id]);
      if (!result) {
        setAiState("unavailable");
        setSuggestions([]);
        setAiFields(null);
        // Analysis is a bonus, not a gate — the invoice is uploaded either way,
        // and Continue lets the rep move on and assign the seller by hand.
        return;
      }
      setAiState("done");
      setSuggestions(result.users || []);
      setAiFields(result.fields || null);
      const matched = await resolveSuggestedProduct(result.product);
      if (matched) setProduct(matched);
    } catch (e) {
      alert.error("Invoice upload failed — please try again.");
    } finally {
      setUploadingInvoice(false);
    }
  };

  const clearInvoice = () => {
    setInvoiceFile(null);
    setAiState(null);
    setAiFields(null);
    setSuggestions([]);
    setCreateRequest(false);
  };

  // ── Field 2: Client PO (existing Sell Request) ─────────────────────────────
  // Selecting a request ARMS ITS LINK. It used to only fill the fields, so the
  // saved listing was never pointed at the Client PO the rep had chosen —
  // attachListingToRequest ran for a request created in-flow and nothing else.
  const onRequestSelect = async (req) => {
    // Either/or: picking a request clears the invoice side.
    clearInvoice();
    setPendingAttachId(req.id);
    try {
      const full = await fetchRequest(req.id);
      setSelectedRequest(full);
      if (full.client?.id) {
        setSeller({ id: full.client.id, name: personName(full.client) });
        setSuggestions(full.client ? [full.client] : []);
      }
      if (full.product_id || full.product?.id) {
        const matched = await resolveSuggestedProduct({ id: full.product_id || full.product?.id });
        if (matched) setProduct(matched);
      }
    } catch (e) {
      setSelectedRequest(req); // list payload still shows the summary
      alert.info("Loaded the request summary only — details could not be fetched.");
    }
  };

  const clearRequest = () => {
    setSelectedRequest(null);
    setAgreements({});
    setSuggestions([]);
    // Dropping the selection drops the link with it, back to whatever this flow
    // raised itself. Only the LINK goes: a request created a moment ago is left
    // alone rather than deleted, and can be reattached from the edit screen.
    setPendingAttachId(createdRequestId);
  };

  const poUrl =
    selectedRequest?.xero_purchase_order_file_url ??
    selectedRequest?.xero_purchase_order_file_id ??
    null;

  // ── Create the seller's Sell Request NOW ───────────────────────────────────
  // "Create Sell Request" creates it and shows it as linked, rather than arming
  // a flag the save handler acts on later. The listing does not exist yet, so
  // only the LINK waits for the save.
  const createRequestBlockedNote =
    createdRequestId
      ? `Sell Request #${createdRequestId} was already raised for this listing. Unlinking it here does not delete it — reattach it from the listing's edit screen.`
      : !seller?.id
        ? "Assign the seller first — the Sell Request is raised in their name."
        : !product?.id
          ? "Pick the product first — a Sell Request cannot be created without one."
          : null;

  const createRequestNow = async () => {
    const created = await createDoneSellRequest({
      clientId: seller.id,
      productId: product.id,
    });
    setPendingAttachId(created.id);
    setCreatedRequestId(created.id);
    setCreateRequest(false); // it exists now; nothing left for the save to create
    try {
      setSelectedRequest(await fetchRequest(created.id));
    } catch (e) {
      setSelectedRequest(created);
    }
  };

  // ── Agreements ──────────────────────────────────────────────────────────────
  const loadAgreement = async (key) => {
    if (!selectedRequest?.id) return;
    const fetcher = key === "consignment" ? fetchConsignmentAgreement : fetchPurchasingAgreement;
    setAgreements((a) => ({ ...a, [key]: { loading: true } }));
    try {
      const { file_id, signed_url } = await fetcher(selectedRequest.id);
      setAgreements((a) => ({ ...a, [key]: { url: signed_url || file_id } }));
    } catch (e) {
      setAgreements((a) => ({ ...a, [key]: { error: `Could not fetch the ${key} agreement.` } }));
    }
  };

  // Auto-surface the linked request's documents (see useAutoContractDocs):
  // existing agreements fetch on link; the purchasing agreement and the Xero PO
  // auto-generate when the request has a quote and they don't exist yet.
  const [poAutoNote, setPoAutoNote] = useState(null);
  useAutoContractDocs({
    request: selectedRequest,
    kind: "purchasing",
    agreements,
    loadAgreement,
    poPresent: !!poUrl,
    onPo: ({ ok, data, message }) => {
      if (ok) {
        setPoAutoNote(null);
        setSelectedRequest((prev) =>
          prev
            ? {
                ...prev,
                xero_purchase_order_file_id: data.xero_purchase_order_file_id,
                xero_purchase_order_file_url: data.xero_purchase_order_file_url,
              }
            : prev
        );
      } else {
        setPoAutoNote(message);
      }
    },
  });

  // ── Prefill into the form (only fills untouched fields) ────────────────────
  // Precedence: a selected Sell Request is the richest source (it carries the
  // Confirm Request answers), then Gemini's invoice reading, then the flow's
  // own constants. Owned By is SEEDED by the flow ("owned" = the store itself,
  // the usual case for bought-in stock) but stays the rep's to change: `enforce`
  // writes page state into Formik and `reportFields` reports Formik's live value
  // back, so the intake dropdown and the Listings-section field are one value.
  // It has to go through `enforce` rather than `prefill` because
  // FormHelper.initialValues hardcodes internal_product = "owned" — the field is
  // never empty, and a soft prefill could never change it.
  // The assigned seller IS the listing's supplier: `supplier`/`supplier_id`
  // are ENFORCED (not prefilled) so changing the seller mid-flow updates the
  // listing, and `clear` empties both on an explicit unassign — the raw
  // Supplier picker this replaces was removed from Internal Details
  // 2026-09-04. supplier_id feeds trade routes, order export and PnL, so the
  // name must never travel without the id.
  const enforce = useMemo(
    () => ({
      internal_product: ownedBy,
      is_repair: isRepair,
      ...(seller?.id ? { supplier: seller.name, supplier_id: seller.id } : {}),
    }),
    [ownedBy, isRepair, seller?.id, seller?.name]
  );

  const prefill = useMemo(() => ({
    ...listingPrefillFromRequest(selectedRequest),
    invoices: invoiceFile ? [invoiceFile] : undefined,
    serial_number: selectedRequest?.serial_number ?? aiFields?.serial_number,
    warranty_date: aiFields?.warranty_date,
    buying_price: listingPrefillFromRequest(selectedRequest).buying_price ?? aiFields?.buying_price,
    buying_currency:
      listingPrefillFromRequest(selectedRequest).buying_currency ?? aiFields?.buying_currency,
    year: selectedRequest?.year ?? aiFields?.year,
  }), [invoiceFile, aiFields, selectedRequest]);

  // An explicit unassign has to empty both stored values — the only bucket
  // allowed to write an empty value. A never-assigned seller is a no-op clear.
  const clear = seller === null ? ["supplier", "supplier_id"] : undefined;

  // ── Save orchestration ──────────────────────────────────────────────────────
  // One rule for both halves of the Sell Request question — see
  // sellRequestActionOnSave. The Client-PO route maps to "existing": that
  // request is the listing's SOURCE and already exists, so it is linked and
  // never duplicated. Only the supplier-invoice route raises one.
  const requestAction = sellRequestActionOnSave({
    option: source === "po" ? "existing" : (createRequest ? "b2b" : null),
    pendingAttachId,
    createdRequestId,
  });

  const afterSubmit = async (saved) => {
    const savedId = saved?.id || saved?.listing?.id;
    const intent = intentRef.current;

    if (intent === "publish" && savedId) {
      try {
        await activateListing(savedId);
      } catch (e) {
        alert.error("Listing saved but publishing failed — activate it from the listing card.");
      }
    }

    // A request created earlier in the flow, or the Client PO the rep chose,
    // only needs its link now — the listing did not exist when it was raised.
    if (requestAction === "attach" && savedId) {
      try {
        await attachListingToRequest(pendingAttachId, savedId);
      } catch (e) {
        alert.info(
          `Listing saved — Sell Request #${pendingAttachId} could not be linked to it automatically.`
        );
      }
    } else if (requestAction === "create" && seller?.id && savedId) {
      // Supplier-invoice path, where the request is raised for the rep rather
      // than asked for: close the loop with a terminal Sell Request for the
      // seller. Client-PO path: the request already exists — listing only.
      if (!product?.id) {
        alert.info(
          "Listing saved — no Sell Request was created: it needs a product, and none was selected."
        );
      } else {
        try {
          await createDoneSellRequest({
            clientId: seller.id,
            productId: product.id,
            listingId: savedId,
          });
        } catch (e) {
          alert.info("Listing saved — the linked Sell Request could not be created automatically.");
        }
      }
    }

    if (savedId) history.push(`/product/${savedId}${iframeQueryParams ? `?${iframeQueryParams}` : ""}`);
  };

  // ── Flow wiring into RCListingForm ─────────────────────────────────────────
  const topSections = (
    <>
      <FlowSection
        icon={<IDoc />}
        label="Origin"
        isOpen={openSection === "upload"}
        onToggle={() => toggleSection("upload")}
        summary={
          [
            invoiceFile
              ? `Supplier invoice · ${invoiceFile.name}`
              : selectedRequest
                ? `Client PO · ${
                    [selectedRequest.product?.reference_number, `#${selectedRequest.id}`]
                      .filter(Boolean)
                      .join(" · ")
                  }`
                : null,
            // In Repair is set in this section but read much later (the card,
            // the Cost tab), so it has to survive the collapse — otherwise the
            // only way to check it is to reopen the section on spec.
            isRepair ? "In repair" : null,
          ]
            .filter(Boolean)
            .join(" · ") || null
        }
      >
        {/* Radio cards, the same shape ASSIGN CONSIGNOR uses: the two sources are
            mutually exclusive, so only the chosen one's fields are on screen.
            That also means neither column can be resized by the other's
            content — the old side-by-side layout was where the upload box got
            squeezed by the search results. */}
        <RadioGroup
          layout="cards"
          legend="Paperwork source"
          hideLegend
          value={source}
          onChange={(key) => pickSource(key)}
        >
          {SOURCES.map((s) => (
            <Radio key={s.key} value={s.key} label={s.label} description={s.hint} />
          ))}
        </RadioGroup>

        {/* The chosen source's fields, full width beneath the tabs. */}
        <div className="rc-flow__source-body">
          {source === "b2b" && (
            <>
              <IntakeFileBox
                label=""
                accept="application/pdf"
                file={invoiceFile}
                uploading={uploadingInvoice}
                onPick={onInvoicePick}
                onClear={clearInvoice}
              />
              <AiStatus state={invoiceFile ? aiState : null} empty={aiEmpty} />
            </>
          )}

          {source === "po" && (
            <>
              <SellRequestSearch
                selected={selectedRequest}
                onSelect={onRequestSelect}
                onClear={clearRequest}
              />
              {selectedRequest && (
                poUrl ? (
                  <div className="rc-lp__detail-pdf rc-flow__po-preview">
                    <PdfShower fileId={poUrl} />
                  </div>
                ) : (
                  <div className="rc-flow__hint">
                    This Sell Request has no Xero purchase order yet — generate it from the request flow.
                  </div>
                )
              )}
            </>
          )}
        </div>

        {/* Last things in the section, in this order deliberately: the rep sets
            Owned By and In Repair and then commits. Continue replaces the old
            implicit collapse-on-upload, which hid them before they could be
            used. Owned By is the SAME field as Listings ▸ Owned By, not a copy
            of it — see `enforce` / `reportFields`. */}
        <OwnedByField
          value={ownedBy}
          onChange={setOwnedBy}
          storeName={settingsCtx?.settings?.name}
        />
        <RepairToggleField value={isRepair} onChange={setIsRepair} />

        {/* Outside both tabs: neither source is mandatory — a rep may have the
            watch in hand with the paperwork to follow. Skip goes straight to
            Assign Seller and leaves CONTRACTS empty to fill in later. */}
        <div className="rc-flow__source-actions">
          {invoiceFile || selectedRequest ? (
            <ButtonGroup align="end">
              <Button data-btn="origin-confirm" onClick={continueFromUpload}>
                Continue
              </Button>
            </ButtonGroup>
          ) : (
            <>
              <span className="rc-flow__skip-note">
                No paperwork yet? You can add it later in Contracts.
              </span>
              <ButtonGroup align="end">
                <Button variant="ghost" data-btn="origin-cancel" onClick={advanceToSeller}>
                  Skip
                </Button>
              </ButtonGroup>
            </>
          )}
        </div>
      </FlowSection>

      <FlowSection
        icon={<IUserIcon />}
        label="Assign Seller"
        isOpen={openSection === "seller"}
        onToggle={() => toggleSection("seller")}
        summary={seller?.name ?? null}
      >
        <AssignPersonPicker
          label="Seller"
          suggestions={suggestions}
          suggestionsNote={
            suggestions.length > 0
              ? (invoiceFile ? "Suggested from the uploaded invoice." : "Picked up from the selected Sell Request.")
              // UPLOAD collapses on Continue, taking its AI status strip off
              // screen — so say here why there are no suggestions to click,
              // rather than leaving an unexplained gap.
              : invoiceFile && aiState === "unavailable"
                ? "The invoice couldn't be read automatically — search for the seller below, or add them as a new user."
                : invoiceFile && aiState === "done"
                  ? "No matching users were found in this store for that invoice."
                  : null
          }
          value={seller}
          onChange={setSeller}
          createTitle="New Seller"
        />
      </FlowSection>
    </>
  );

  // Internal Details shows the linked Sell Request in place of the raw
  // Supplier picker — selecting one here populates the same fields as the
  // UPLOAD section's Client-PO search.
  const supplierSlot = (
    <LinkedRequestSlot
      selected={selectedRequest}
      onSelect={onRequestSelect}
      onClear={clearRequest}
      onCreate={createRequestNow}
      createBlockedNote={createRequestBlockedNote}
    />
  );

  const agreementSlots = [
    {
      key: "purchasing",
      label: "Purchasing Agreement",
      url: agreements.purchasing?.url,
      loading: agreements.purchasing?.loading,
      error: agreements.purchasing?.error,
      onLoad: selectedRequest?.id ? () => loadAgreement("purchasing") : null,
    },
    {
      key: "consignment",
      label: "Consignment Agreement",
      url: agreements.consignment?.url,
      loading: agreements.consignment?.loading,
      error: agreements.consignment?.error,
      onLoad: selectedRequest?.id ? () => loadAgreement("consignment") : null,
    },
  ];

  const flow = useMemo(() => ({
    title: "New Stock",
    product,
    // The selector's own choice, so "Create Sell Request" has a product_id even
    // when the invoice reading auto-picked nothing. Guarded: the form also
    // reports back the product WE pushed in, and re-setting it here would loop.
    onProductChange: (picked) => {
      if ((picked?.id ?? null) !== (product?.id ?? null)) setProduct(picked);
    },
    prefill,
    enforce,
    clear,
    // Mirror the Cost-tab In Repair toggle and the Listings-section Owned By
    // dropdown back out to page state, so the intake-section copies reflect a
    // change made there. enforce carries the other direction; both write the
    // same value, so no loop.
    reportFields: ["is_repair", "internal_product"],
    onReport: (v) => {
      setIsRepair(!!v.is_repair);
      if (v.internal_product) setOwnedBy(v.internal_product);
    },
    topSections,
    supplierSlot,
    openSectionRequest: formSectionRequest,
    renderContractsSection: ({ openSection: formOpen, toggleSection: formToggle }) => (
      <ContractsSection
        openSection={formOpen}
        toggleSection={formToggle}
        poUrl={poUrl}
        poEmptyNote={
          poAutoNote ??
          (invoiceFile
            ? "Supplier-invoice intake — the purchase order appears here once the linked Sell Request generates one."
            : undefined)
        }
        agreementSlots={agreementSlots}
      />
    ),
    renderStatusSection: ({ submitForm }) => (
      <ListingStatusSection submitForm={submitForm} intentRef={intentRef} />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [product, prefill, enforce, clear, topSections, supplierSlot, formSectionRequest, poUrl, poAutoNote, agreements, selectedRequest, invoiceFile, isRepair, ownedBy, settingsCtx]);

  return (
    <>
      <Helmet>
        <title>New Stock — Retail Circle</title>
        <meta name="robots" content="noindex, nofollow, noarchive" />
      </Helmet>
      <RCListingForm listing={{}} afterSubmit={afterSubmit} flow={flow} />
    </>
  );
};

export default RCStockListingCreate;
